import { colors } from "src/assets";
import React from "react";
import {
  StyleSheet,
  Text,
  View,
  TouchableOpacity,
  ActivityIndicator,
  StyleProp,
  ViewStyle,
  TextStyle,
} from "react-native";
import { Icon } from "src/components";
import { IconName } from "./icon/index";

export type ButtonProps = {
  containerStyle?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
  title?: string;
  icon?: IconName;
  iconSize?: number;
  iconColor?: string;
  iconStyle?: StyleProp<ViewStyle>;
  variant?: "primary" | "secondary" | "link" | "icon";
  loading?: boolean;
  disabled?: boolean;
  onPress?: () => void;
};

export const Button: React.FC<ButtonProps> = ({
  containerStyle,
  textStyle,
  title,
  icon,
  iconSize,
  iconColor,
  iconStyle,
  variant,
  loading,
  disabled,
  onPress,
}) => {
  let localStyles: any = primaryStyles;
  if (variant === "secondary") {
    localStyles = secondaryStyles;
  } else if (variant === "link") {
    localStyles = linkStyles;
  } else if (variant === "icon") {
    localStyles = iconStyles;
  }
  return (
    <TouchableOpacity
      style={[
        localStyles.container,
        loading || disabled ? localStyles.loading : {},
        containerStyle || {},
      ]}
      disabled={loading || disabled}
      onPress={() => {
        if (onPress) {
          onPress();
        }
      }}
    >
      <View style={localStyles.textContainer}>
        {icon ? (
          <Icon
            name={icon}
            size={iconSize || 24}
            style={[localStyles.icon, iconStyle]}
            color={iconColor}
          />
        ) : null}
        {title ? (
          <Text style={[localStyles.text, textStyle]}>{title}</Text>
        ) : null}
        {loading ? (
          <ActivityIndicator
            style={localStyles.loadingIndicatorContainer}
            color={localStyles.text.color}
          />
        ) : null}
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 12,
    marginBottom: 16,
  },
  icon: {
    marginRight: 8,
  },
  textContainer: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
  },
  text: {
    fontSize: 15,
    fontWeight: "bold",
    lineHeight: 20,
    color: colors.chokuBlack,
  },
  loadingIndicatorContainer: {
    marginLeft: 8,
  },
  loading: {
    opacity: 0.5,
  },
});

const primaryStyles = StyleSheet.create({
  container: {
    ...styles.container,
    backgroundColor: colors.chokuRed,
    borderColor: colors.chokuRed,
    borderWidth: 2,
    borderRadius: 4,
  },
  icon: {
    ...styles.icon,
  },
  textContainer: {
    ...styles.textContainer,
  },
  text: {
    ...styles.text,
    color: colors.white,
  },
  loadingIndicatorContainer: {
    ...styles.loadingIndicatorContainer,
  },
  loading: {
    ...styles.loading,
  },
});

const secondaryStyles = StyleSheet.create({
  container: {
    ...styles.container,
    backgroundColor: colors.white,
    borderColor: colors.chokuRed,
    borderWidth: 2,
    borderRadius: 4,
  },
  icon: {
    ...styles.icon,
  },
  textContainer: {
    ...styles.textContainer,
  },
  text: {
    ...styles.text,
    color: colors.chokuRed,
  },
  loadingIndicatorContainer: {
    ...styles.loadingIndicatorContainer,
  },
  loading: {
    ...styles.loading,
  },
});

const linkStyles = StyleSheet.create({
  container: {
    ...styles.container,
  },
  icon: {
    ...styles.icon,
  },
  textContainer: {
    ...styles.textContainer,
    borderBottomColor: colors.chokuGray,
    borderBottomWidth: 1,
  },
  text: {
    ...styles.text,
    color: colors.chokuGray,
    fontSize: 14,
  },
  loadingIndicatorContainer: {
    ...styles.loadingIndicatorContainer,
  },
  loading: {
    ...styles.loading,
  },
});

const iconStyles = StyleSheet.create({
  container: {
    ...styles.container,
    paddingVertical: 0,
    marginBottom: 0,
  },
  icon: {
    ...styles.icon,
    marginRight: 0,
  },
  textContainer: {
    ...styles.textContainer,
  },
  text: {
    ...styles.text,
  },
  loadingIndicatorContainer: {
    ...styles.loadingIndicatorContainer,
  },
  loading: {
    ...styles.loading,
  },
});
